import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { Button, Col, DatePicker, Divider, Form, Input, Radio, Row, Select } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";

import styles from "@/components/Pledge/CDDForm/CDDForm.module.css";
import DynamicField from "@/components/Pledge/CDDForm/DynamicFields";
import { JSON_DATA, ROUTES_URL } from "@/constant";
import { getOccupationList } from "@/services/Auth/AuthApi";
import { getAllCountries } from "@/services/Location";
import { getCddList } from "@/services/Pledge/PledgeApis";
import { useAuthStore } from "@/stores/authStore";
import { useRenewStore } from "@/stores/renewStore";
import { useMasterStore } from "@/stores/useMasterStore";
import { formatAmount } from "@/utils/formatAmount";

interface CDDForm {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const CDDForm: React.FC<CDDForm> = ({ setLoading }) => {
  const { idTypes } = useMasterStore();
  const idtypeOptions = idTypes.map((val) => ({ id: val.id, value: val.id_type_name }));
  const navigate = useNavigate();
  const { currency } = useAuthStore();
  const customerData = useAuthStore((state) => state?.customer);
  const pledgeData = useRenewStore((state) => state?.selectedPledgeData);
  const update = useRenewStore((state) => state?.update);
  const reset = useRenewStore((state) => state?.reset);
  const pledgeAmount = pledgeData?.offered_pledge_amount ?? 0;
  const [form] = Form.useForm();
  const [occupationList, setOccupationList] = useState<string[]>([]);
  const [nationalities, setNationalities] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch] = useDebounce(searchValue, 500);
  const [ecdd, setEcdd] = useState(false);
  const [countries, setCountries] = useState<string[]>([]);

  const fetchOccupationList = async (): Promise<void> => {
    try {
      const params = {
        ...(searchValue && { search_key: searchValue }),
      };
      const queryString = new URLSearchParams(params).toString();
      const response = await getOccupationList(queryString);

      setOccupationList(response?.data?.map((el) => el.occupation_details) || []);

      const res = await axios.get(JSON_DATA.NATIONALITIES);
      setNationalities(res.data);

      void getAllCountries()
        .then((countries) => setCountries(countries))
        .catch((err) => {
          console.error("Error fetching countries", err);
        });
    } catch (error) {
      console.error("Error fetching occupation list:", error);
    }
  };

  const fetchPledgeCdd = async (): Promise<void> => {
    try {
      setLoading(true);
      const { data } = await getCddList({
        pledge_id: pledgeData?.id,
      });
      const cdd_info = data?.[0]?.cdd_info;
      const cdd_type = data?.[0]?.cdd_type;
      if (cdd_info) {
        setEcdd(cdd_type == "ecdd");
        form.setFieldsValue({
          ...cdd_info,
          ...(cdd_info?.periodOfPep
            ? {
                periodOfPep: cdd_info.periodOfPep.map((d: string) => dayjs(d)),
              }
            : {}),
          ...(cdd_info?.ownerDob
            ? {
                ownerDob: dayjs(cdd_info.ownerDob),
              }
            : {}),
        });
      }
    } catch (error) {
      console.error("Error fetching occupation list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchPledgeCdd();
    void fetchOccupationList();
  }, [debouncedSearch]);

  const getShouldUpdate = (key?: string) => {
    if (!key) return () => false;

    return (prev: any, curr: any) => prev[key] !== curr[key];
  };

  const handleOccupationSearch = (value: string): void => {
    setSearchValue(value);
  };

  const renderOccupationOptions = (): React.ReactNode =>
    occupationList.map((opt: string, index: number) => (
      <Select.Option key={`${opt}-${index}`} value={opt}>
        {opt}
      </Select.Option>
    ));

  const renderRadioOptions = (options: string[], maskPii = false): React.ReactNode => (
    <Radio.Group
      className={styles.radioGroup}
      {...(maskPii ? { "data-dd-privacy": "mask" as const } : {})}
    >
      {options.map((opt) => (
        <Radio key={opt} value={opt}>
          {opt}
        </Radio>
      ))}
    </Radio.Group>
  );

  const pepAdditionalSection = (): React.ReactNode => {
    return (
      <Form.Item noStyle shouldUpdate={(prev, curr) => prev["isPEP"] !== curr["isPEP"]}>
        {({ getFieldValue }) => {
          const isPEP = getFieldValue("isPEP") === "Yes";
          if (!isPEP) return null;
          return (
            <div className="pl-3 mb-3 mr-3 w-full">
              <Divider
                data-testid="filter-serial-label"
                className={styles.filterLable}
                orientation="left"
                orientationMargin="0"
              >
                Section D - Enhanced CDD (ECDD) measures
              </Divider>

              <Row gutter={24}>
                <Col span={12} className="mb-5 flex gap-1">
                  <span className={styles.fieldName}>{"a)"}</span>
                  <div className="flex flex-col">
                    <span className={styles.fieldName}>
                      Are the enhanced CDD measures carried out for politically exposed person (PEP)
                      / suspicious character?
                    </span>
                  </div>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="cddmeasuresCarried"
                    className={styles.customForm}
                    valuePropName={"value"}
                    rules={[
                      {
                        required: true,
                        message: `This field is required`,
                      },
                    ]}
                  >
                    <Radio.Group className={styles.radioGroup}>
                      {["Yes", "No"].map((opt: string) => (
                        <Radio key={opt} value={opt}>
                          {opt}
                        </Radio>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={12} className="mb-5 flex gap-1">
                  <span className={styles.fieldName}> </span>
                  <div className="flex flex-col">
                    <span className={styles.fieldName}>Pawner is a PEP?</span>
                  </div>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="pawnerIsPep"
                    className={styles.customForm}
                    valuePropName={"value"}
                    data-dd-privacy="mask"
                    rules={[
                      {
                        required: true,
                        message: `This field is required`,
                      },
                    ]}
                  >
                    <Radio.Group className={styles.radioGroup}>
                      {["Yes", "No"].map((opt: string) => (
                        <Radio key={opt} value={opt}>
                          {opt}
                        </Radio>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) => prev["pawnerIsPep"] !== curr["pawnerIsPep"]}
              >
                {({ getFieldValue }) => {
                  const pawnerIsPep = getFieldValue("pawnerIsPep");
                  return pawnerIsPep === "Yes" ? (
                    <>
                      <Row gutter={24}>
                        <Col span={12} className="mb-5 flex gap-1">
                          <span className={styles.fieldName}> </span>
                          <div className="flex flex-col">
                            <span className={styles.fieldName}>Name of PEP</span>
                          </div>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="nameOfPep"
                            className={styles.customForm}
                            valuePropName={"value"}
                            rules={[
                              {
                                required: true,
                                message: `This field is required`,
                              },
                            ]}
                          >
                            <Input data-dd-privacy="mask" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={24}>
                        <Col span={12} className="mb-5 flex gap-1">
                          <span className={styles.fieldName}> </span>
                          <div className="flex flex-col">
                            <span className={styles.fieldName}>Organizer / Employer</span>
                          </div>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="pepEmployer"
                            className={styles.customForm}
                            valuePropName={"value"}
                            rules={[
                              {
                                required: true,
                                message: `This field is required`,
                              },
                            ]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={24}>
                        <Col span={12} className="mb-5 flex gap-1">
                          <span className={styles.fieldName}> </span>
                          <div className="flex flex-col">
                            <span className={styles.fieldName}>Occupation / Position</span>
                          </div>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="pepOccupation"
                            className={styles.customForm}
                            valuePropName={"value"}
                            rules={[
                              {
                                required: true,
                                message: `This field is required`,
                              },
                            ]}
                          >
                            <Select
                              placeholder="Select"
                              className={styles.customSelect}
                              showSearch
                              optionFilterProp="label"
                              filterOption={false}
                              data-dd-privacy="mask"
                              onSearch={handleOccupationSearch}
                            >
                              {renderOccupationOptions()}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col span={12} className="mb-5 flex gap-1">
                          <span className={styles.fieldName}> </span>
                          <div className="flex flex-col">
                            <span className={styles.fieldName}>
                              Describe the nature of Prominent Public Function that the PEP is
                              entrusted with
                            </span>
                          </div>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="natureOfPep"
                            className={styles.customForm}
                            valuePropName={"value"}
                            rules={[
                              {
                                required: true,
                                message: `This field is required`,
                              },
                            ]}
                          >
                            {renderRadioOptions(
                              [
                                "Domestic PEP (Singapore)",
                                "Foreign PEP",
                                "PEP from Intemational Organisation",
                              ],
                              true,
                            )}
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={24}>
                        <Col span={12} className="mb-5 flex gap-1">
                          <span className={styles.fieldName}> </span>
                          <div className="flex flex-col">
                            <span className={styles.fieldName}>
                              Period of time which the person is/was a PEP: [dd-mm-yyyy]
                            </span>
                          </div>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="periodOfPep"
                            className={styles.customForm}
                            valuePropName={"value"}
                            rules={[
                              {
                                required: true,
                                message: `This field is required`,
                              },
                            ]}
                          >
                            <DatePicker.RangePicker
                              format="DD-MM-YYYY"
                              className="h-[45px]"
                              data-dd-privacy="mask"
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={24}>
                        <Col span={12} className="mb-5 flex gap-1">
                          <span className={styles.fieldName}> </span>
                          <div className="flex flex-col">
                            <span className={styles.fieldName}>Source of Wealth</span>
                          </div>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="sourceOfWealth"
                            className={styles.customForm}
                            valuePropName={"value"}
                            rules={[
                              {
                                required: true,
                                message: `This field is required`,
                              },
                            ]}
                          >
                            {renderRadioOptions([
                              "Salary",
                              "Business Profits",
                              "Gift / Inheritance",
                              "Investment Earnings",
                              "Others",
                            ])}
                          </Form.Item>
                          <Form.Item
                            name={`sourceOfWealth_other`}
                            rules={[
                              {
                                required: form.getFieldValue("sourceOfWealth") === "Others",
                                message: "Please specify other value",
                              },
                            ]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  ) : (
                    <>
                      <Row gutter={24}>
                        <Col span={12} className="mb-5 flex gap-1">
                          <span className={styles.fieldName}> </span>
                          <div className="flex flex-col">
                            <span className={styles.fieldName}>Name</span>
                          </div>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="pawnerPepName"
                            className={styles.customForm}
                            valuePropName={"value"}
                            rules={[
                              {
                                required: true,
                                message: `This field is required`,
                              },
                            ]}
                          >
                            <Input data-dd-privacy="mask" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={24}>
                        <Col span={12} className="mb-5 flex gap-1">
                          <span className={styles.fieldName}> </span>
                          <div className="flex flex-col">
                            <span className={styles.fieldName}>
                              If pawner is not the PEP, describe the nature of person's relationship
                              to PEP
                            </span>
                          </div>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="pepRelation"
                            className={styles.customForm}
                            valuePropName={"value"}
                            rules={[
                              {
                                required: true,
                                message: `This field is required`,
                              },
                            ]}
                          >
                            {renderRadioOptions(["Family Member", "Cloee Associate"], true)}
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  );
                }}
              </Form.Item>
            </div>
          );
        }}
      </Form.Item>
    );
  };

  const cddForm = [
    {
      id: "sectionA_customer_info",
      section: "Section A - Customer Information",
      fields: [
        {
          id: "fullName",
          label: "Full Name",
          type: "span",
          value: customerData?.name,
        },
        {
          id: "customerId_nric",
          label: "Customer Id: " + customerData?.id_type,
          type: "span",
          value: customerData ? customerData.id_number : "-",
        },
        {
          id: "gender",
          label: "Gender",
          type: "radio",
          options: ["Male", "Female"],
        },
        {
          id: "occupation",
          label: "Occupation",
          type: "select",
          options: occupationList,
        },
        {
          id: "occupationDetails",
          label: "For Business, Self-Employed and others, please provide details.",
          type: "text",
        },
      ],
    },
    {
      id: "sectionB_cdd",
      section: "Section B - Customer Due Diligence",
      fields: [
        {
          id: "loanAmount",
          labelIndex: "a)",
          label: `Amount of loan (${currency})`,
          type: "span",
          value: formatAmount(Number(pledgeAmount)),
        },
        {
          id: "loanPurpose",
          labelIndex: "b)",
          label: "Purpose of loan?",
          type: "radioWithOther",
          options: ["General Expenses", "Business Expenses", "Others"],
        },
        {
          id: "isAbove18",
          labelIndex: "c)",
          label: "Is the pawner above 18 years old?",
          type: "radio",
          options: ["Yes", "No"],
        },
        {
          id: "isLawfulOwner",
          labelIndex: "d)",
          label: "Is the pawner the lawful owner of the pledge?",
          type: "radio",
          options: ["Yes", "No"],
        },
        {
          id: "ownerAuthorization",
          labelIndex: "d1)",
          label: "Authorization received from owner?",
          type: "radio",
          options: ["Yes", "No"],
          conditionalKey: "isLawfulOwner",
          conditionalValue: "No",
        },
        {
          id: "pledgeAcquisitionMethod",
          labelIndex: "e)",
          label: "How did the pawner obtain the pledge?",
          type: "radioWithOther",
          options: ["Own Purchase", "Received as a Gift", "Purchased as a Gift", "Others"],
        },
        {
          id: "isFamiliarWithPledge",
          labelIndex: "f)",
          label: "Is the pawner familiar with the pledge?",
          type: "radio",
          options: ["Yes", "No"],
        },
        {
          id: "remarks_1",
          labelIndex: "f1)",
          label: "Remarks",
          type: "textarea",
          placeholder: "Type",
          required: false,
        },
        {
          id: "isNotCounterfeit",
          labelIndex: "g)",
          label: "Does the pawner confirm that the pledge is not a counterfeit?",
          type: "radio",
          options: ["Yes", "No"],
        },
        {
          id: "isNotFraudulent",
          labelIndex: "h)",
          label:
            "Does the pawner confirm that the pledge was not obtained through fraudulent / dishonest means?",
          type: "radio",
          options: ["Yes", "No"],
        },
        {
          id: "isNotMoneyLaundering",
          labelIndex: "i)",
          label:
            "Does the pawner confirm that this transaction does not involve any money laundering activities?",
          type: "radio",
          options: ["Yes", "No"],
        },
        {
          id: "isNotTerrorismFinancing",
          labelIndex: "j)",
          label:
            "Does the pawner confirm that this transaction does not involve any terrorism financing activities?",
          type: "radio",
          options: ["Yes", "No"],
        },
        {
          id: "remarks_2",
          labelIndex: "k)",
          label: "Remarks",
          type: "textarea",
          placeholder: "Type",
          required: false,
        },
      ],
    },
    {
      id: "sectionC_staff_review",
      section: "Section C - Staff Review",
      fields: [
        {
          id: "isSoundMind",
          labelIndex: "a)",
          label: "Does the pawner appear to be of sound mind?",
          type: "radio",
          options: ["Yes", "No"],
        },
        {
          id: "isUnderThreat",
          labelIndex: "b)",
          label: "Does the pawner appear to be under any form of threat / coercion?",
          type: "radio",
          options: ["No", "Yes"],
        },
        {
          id: "isDeclarationTrue",
          labelIndex: "c)",
          label:
            "Does the pawner confirm that the declarations made are true and accurate at all times?",
          type: "radio",
          options: ["Yes", "No"],
        },
      ],
    },
    {
      id: "sectionD_risk_assessment",
      section: "Risk Assessment",
      fields: [
        {
          id: "transactionRisk",
          labelIndex: "a)",
          label: "Risk assessment of transactions",
          type: "radio",
          options: ["Low Risk", "High Risk"],
          disabled: true,
        },
      ],
    },
  ];

  const ecddForm = [
    {
      id: "sectionA_customer_info",
      section: "Section A - Customer Information",
      fields: [
        {
          id: "fullName",
          label: "Full Name",
          type: "span",
          value: customerData?.name,
        },
        {
          id: "customerId_nric",
          label: "Customer Id: " + customerData?.id_type,
          type: "span",
          value: customerData ? customerData.id_number : "-",
        },
        {
          id: "nationality",
          label: "Nationality",
          type: "select",
          options: nationalities,
          disabled: true,
        },
        {
          id: "residentialAddress",
          label: "Residential Address",
          type: "span",
          value: customerData?.address_line_1,
        },
        {
          id: "dateOfBirth",
          label: "Date Of Birth",
          type: "span",
          value: customerData?.date_of_birth,
        },
        {
          id: "contactNumber",
          label: "Contact Number",
          type: "span",
          value: `${customerData?.dial_code} ${customerData?.mobile_number}`,
        },
        {
          id: "occupation",
          label: "Occupation",
          type: "select",
          options: occupationList,
        },
        {
          id: "occupationDetails",
          label: "For Business, Self-Employed and others, please provide details.",
          type: "text",
        },
      ],
    },
    {
      id: "sectionB_pawner_info",
      section:
        "Section B - If pawner is not the owner of the item, please provide owner details in Section (B)",
      fields: [
        {
          id: "pawnerNotOwner",
          label: "Pawner is NOT the owner of item:",
          type: "checkbox",
        },
        {
          id: "ownerFullName",
          label: "Owner Full Name",
          type: "text",
          conditionalKey: "pawnerNotOwner",
          conditionalValue: true,
        },
        {
          id: "ownerIdType",
          label: "Owner ID Type",
          type: "select",
          options: idtypeOptions,
          conditionalKey: "pawnerNotOwner",
          conditionalValue: true,
        },
        {
          id: "ownerIdNumber",
          label: "Owner ID Number",
          type: "text",
          conditionalKey: "pawnerNotOwner",
          conditionalValue: true,
        },
        {
          id: "ownerNationality",
          label: "Owner Nationality",
          type: "select",
          options: nationalities,
          conditionalKey: "pawnerNotOwner",
          conditionalValue: true,
        },
        {
          id: "ownerResidentialAddress",
          label: "Owner Residential Address",
          type: "textarea",
          conditionalKey: "pawnerNotOwner",
          conditionalValue: true,
        },
        {
          id: "ownerDob",
          label: "Owner Date of Birth",
          type: "datepicker",
          conditionalKey: "pawnerNotOwner",
          conditionalValue: true,
        },
        {
          id: "ownerContactNumber",
          label: "Owner Contact Number",
          type: "text",
          conditionalKey: "pawnerNotOwner",
          conditionalValue: true,
        },
        {
          id: "ownerAuthorization",
          label: "Authorization received from owner?",
          type: "radio",
          options: ["Yes", "No"],
          conditionalKey: "pawnerNotOwner",
          conditionalValue: true,
        },
      ],
    },
    {
      id: "sectionC_cdd",
      section: "Section C - Customer Due Diligence (CDD measures)",
      fields: [
        {
          id: "loanAmount",
          labelIndex: "a)",
          label: "Amount of loan",
          type: "span",
          value: formatAmount(Number(pledgeAmount)),
        },
        {
          id: "loanPurpose",
          labelIndex: "b)",
          label: "Purpose of loan?",
          type: "radioWithOther",
          options: ["General Expenses", "Business Expenses", "Others"],
        },
        {
          id: "isAbove18",
          labelIndex: "c)",
          label: "Is the pawner above 18 years old?",
          type: "radio",
          options: ["Yes", "No"],
        },
        {
          id: "pledgeAcquisitionMethod",
          labelIndex: "d)",
          label: "How did the pawner obtain the pledge?",
          type: "radioWithOther",
          options: ["Own Purchase", "Received as a Gift", "Purchased as a Gift", "Others"],
        },
        {
          id: "isFamiliarWithPledge",
          labelIndex: "e)",
          label: "Is the pawner familiar with the pledge?",
          type: "radio",
          options: ["Yes", "No"],
        },
        {
          id: "remarks_1",
          labelIndex: "e1)",
          label: "Remarks",
          type: "textarea",
          placeholder: "Type",
          required: false,
        },
        {
          id: "isNotCounterfeit",
          labelIndex: "f)",
          label: "Does the pawner confirm that the pledge is not a counterfeit?",
          type: "radio",
          options: ["Yes", "No"],
        },
        {
          id: "isNotFraudulent",
          labelIndex: "g)",
          label:
            "Does the pawner confirm that the pledge was not obtained through fraudulent / dishonest means?",
          type: "radio",
          options: ["Yes", "No"],
        },
        {
          id: "isNotMoneyLaundering",
          labelIndex: "h)",
          label:
            "Does the pawner confirm that this transaction does not involve any money laundering activities?",
          type: "radio",
          options: ["Yes", "No"],
        },
        {
          id: "isNotTerrorismFinancing",
          labelIndex: "i)",
          label:
            "Does the pawner confirm that this transaction does not involve any terrorism financing activities?",
          type: "radio",
          options: ["Yes", "No"],
        },
        {
          id: "isInSanctionsList",
          labelIndex: "j)",
          label:
            "Name found in the UN Sanctions List (for Terrorist and Proliferation Financing) or Caution Lists?",
          subLabel: "If yes, the transaction must cease and STR to be filed.",
          type: "radio",
          options: ["No", "Yes"],
        },
        {
          id: "isPEP",
          labelIndex: "k)",
          label: "Politically Exposed Person (PEP*)?",
          subLabel:
            "If yes, please complete section (D) and Form for Politically Exposed Person (PEP).",
          type: "radio",
          options: ["No", "Yes"],
          additionalSection: pepAdditionalSection(),
        },
        {
          id: "isFromHighRiskCountry",
          labelIndex: "l)",
          label: "Is customer from one of the FATF High Risk Countries?",
          type: "radio",
          options: ["No", "Yes"],
        },
        {
          id: "highRiskCountry",
          label: "FATF High Risk Country",
          type: "select",
          options: countries,
          conditionalKey: "isFromHighRiskCountry",
          conditionalValue: "Yes",
        },
        {
          id: "loanSuspicion",
          labelIndex: "m)",
          label: "Any sign that indicates the loan is suspicious?",
          subLabel: "If yes, please complete section (E), and determine if STR should be filed.",
          type: "radio",
          options: ["No", "Yes"],
        },
        {
          id: "remarks_2",
          labelIndex: "n)",
          label: "Remarks",
          type: "textarea",
          placeholder: "Type",
          required: false,
        },
      ],
    },
    {
      id: "sectionE_str",
      section: "Section E - Suspicious Transaction Reporting (STR)",
      fields: [
        {
          id: "isStrLodged",
          labelIndex: "a)",
          label:
            "Any STR lodged? (i.e potential terrorist / proliferation financing or other circumstances)",
          type: "radio",
          options: ["Yes", "No"],
        },
      ],
      conditionalKey: "loanSuspicion",
      conditionalValue: "Yes",
    },
    {
      id: "sectionF",
      section: "Section F - Staff Review",
      fields: [
        {
          id: "isSoundMind",
          labelIndex: "a)",
          label: "Does the pawner appear to be of sound mind?",
          type: "radio",
          options: ["Yes", "No"],
        },
        {
          id: "isUnderCoercion",
          labelIndex: "b)",
          label: "Does the pawner appear to be under any form of threat / coercion?",
          type: "radio",
          options: ["No", "Yes"],
        },
        {
          id: "isDeclarationTrue",
          labelIndex: "c)",
          label:
            "Does the pawner confirm that the declarations made are true and accurate at all times?",
          type: "radio",
          options: ["Yes", "No"],
        },
      ],
    },
    {
      id: "sectionG_risk_assessment",
      section: "Risk Assessment",
      fields: [
        {
          id: "transactionRisk",
          labelIndex: "a)",
          label: "Risk assessment of transactions",
          type: "radio",
          options: ["Low Risk", "High Risk"],
          disabled: true,
        },
      ],
    },
  ];

  const formFields = ecdd ? ecddForm : cddForm;

  const groupATrueFields = [
    "isUnderThreat",
    "isInSanctionsList",
    "isPEP",
    "isFromHighRiskCountry",
    "loanSuspicion",
    "isUnderCoercion",
  ];
  const groupBFalseFields = [
    "isAbove18",
    "isLawfulOwner",
    "isFamiliarWithPledge",
    "isNotCounterfeit",
    "isNotFraudulent",
    "isNotMoneyLaundering",
    "isNotTerrorismFinancing",
    "isSoundMind",
    "isDeclarationTrue",
  ];

  const handleValuesChange = (_: any, allValues: any) => {
    const anyTrueInGroupA = groupATrueFields.some((field) => allValues[field] === "Yes");

    const anyFalseInGroupB = groupBFalseFields.some((field) => allValues[field] === "No");

    const risk = anyTrueInGroupA || anyFalseInGroupB ? "High Risk" : "Low Risk";

    form.setFieldsValue({
      transactionRisk: risk,
    });
  };

  const handleSubmit = async (values: any): Promise<void> => {
    try {
      update({
        cddInfo: values,
        cddType: ecdd ? "ecdd" : "cdd",
      });
      navigate(ROUTES_URL.CUSTOMER_CONCENT);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const renderField = (field: any) => (
    <Form.Item key={field.id} noStyle shouldUpdate={getShouldUpdate(field.conditionalKey)}>
      {({ getFieldValue }) => {
        const shouldShow =
          !field.conditionalKey || getFieldValue(field.conditionalKey) === field.conditionalValue;

        if (!shouldShow) return null;

        return (
          <>
            <Col span={12} className="mb-5 flex gap-1">
              {field.labelIndex && <span className={styles.fieldName}>{field.labelIndex}</span>}
              <div className="flex flex-col">
                <span className={styles.fieldName}>{field.label}</span>
                <span className={styles.fieldSubName}>{field.subLabel}</span>
              </div>
            </Col>

            <Col span={12}>
              <DynamicField
                field={field}
                form={form}
                styles={styles}
                setSearchValue={setSearchValue}
              />
            </Col>

            {field.additionalSection}
          </>
        );
      }}
    </Form.Item>
  );

  return (
    <div className={styles.container}>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
        onFinish={handleSubmit}
      >
        {formFields.map((section: any, i) => (
          <div key={section.id}>
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                section?.conditionalKey
                  ? prev[section.conditionalKey] !== curr[section.conditionalKey]
                  : false
              }
            >
              {({ getFieldValue }) => {
                const shouldShow =
                  !section.conditionalKey ||
                  getFieldValue(section.conditionalKey) === section.conditionalValue;

                if (!shouldShow) return null;

                return (
                  <>
                    <Divider
                      data-testid="filter-serial-label"
                      className={styles.filterLable}
                      orientation="left"
                      orientationMargin="0"
                    >
                      {section.section}
                    </Divider>

                    <Row gutter={24}>{section.fields.map(renderField)}</Row>
                  </>
                );
              }}
            </Form.Item>
          </div>
        ))}
        <div className="flex gap-4 justify-end py-4">
          <Button
            className={styles.clearButton}
            onClick={() => {
              reset();
              navigate(-1);
            }}
          >
            Cancel
          </Button>
          <Button htmlType="submit" className={styles.applyButton}>
            Submit
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default CDDForm;
